/*
 * Kubernetes Enhanced Plugin Theme
 * Glassmorphism + Monospace Style
 */

import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

/**
 * 主题颜色常量 - Light Background + Ocean Primary
 */
export const colors = {
  // 背景色 - 浅色系
  bgPrimary: '#f8fafc',
  bgSecondary: '#ffffff',
  bgGlass: 'rgba(255, 255, 255, 0.8)',
  bgGlassHover: 'rgba(255, 255, 255, 0.95)',
  
  // 文字颜色
  textPrimary: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#94a3b8',
  
  // 渐变色 - Ocean 蓝青系
  gradientPrimary: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
  gradientSecondary: 'linear-gradient(135deg, #0ea5e9 0%, #22d3ee 100%)',
  gradientCyan: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
  gradientSuccess: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
  gradientWarning: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
  gradientError: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
  
  // 状态颜色
  statusSuccess: '#10b981',
  statusWarning: '#f59e0b',
  statusError: '#ef4444',
  statusInfo: '#0ea5e9',
  statusPending: '#8b5cf6',
  
  // 边框 - 浅色玻璃效果
  borderGlass: 'rgba(14, 165, 233, 0.2)',
  borderGlassHover: 'rgba(14, 165, 233, 0.4)',
  borderGlow: 'rgba(59, 130, 246, 0.3)',
  
  // 阴影
  shadowGlass: '0 4px 24px rgba(14, 165, 233, 0.1)',
  shadowGlow: '0 0 20px rgba(14, 165, 233, 0.15)',
};

/**
 * 等宽字体
 */
export const monoFont = '"JetBrains Mono", "Fira Code", "SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace';

/**
 * 主样式 Hook
 */
export const useKubernetesEnhancedStyles = makeStyles((theme: Theme) =>
  createStyles({
    // 根容器 - 浅色背景
    root: {
      background: `linear-gradient(180deg, ${colors.bgPrimary} 0%, #e0f2fe 100%)`,
      color: colors.textPrimary,
      padding: theme.spacing(3),
      minHeight: '100%',
      borderRadius: 16,
      fontFamily: monoFont,
      position: 'relative',
      overflow: 'hidden',
      // 背景网格 - 淡蓝色
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          linear-gradient(rgba(14, 165, 233, 0.08) 1px, transparent 1px),
          linear-gradient(90deg, rgba(14, 165, 233, 0.08) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
        pointerEvents: 'none',
      },
    },
    
    // Header 样式
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(3),
      padding: theme.spacing(2, 3),
      background: colors.bgGlass,
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: 16,
      border: `1px solid ${colors.borderGlass}`,
      boxShadow: colors.shadowGlass,
      position: 'relative',
      zIndex: 1,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(3),
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      '& svg': {
        fontSize: '2rem',
        filter: 'drop-shadow(0 0 10px rgba(102, 126, 234, 0.5))',
      },
    },
    headerChip: {
      background: colors.bgGlass,
      backdropFilter: 'blur(10px)',
      color: colors.textPrimary,
      border: `1px solid ${colors.borderGlass}`,
      fontFamily: monoFont,
      fontSize: '0.75rem',
      letterSpacing: '0.5px',
      transition: 'all 0.3s ease',
      '&:hover': {
        background: colors.bgGlassHover,
        borderColor: colors.borderGlassHover,
      },
      '& .MuiChip-icon': {
        color: colors.textSecondary,
      },
    },
    headerChipActive: {
      background: 'rgba(102, 126, 234, 0.2)',
      borderColor: 'rgba(102, 126, 234, 0.5)',
      boxShadow: '0 0 15px rgba(102, 126, 234, 0.3)',
    },
    
    // 卡片样式 - 浅色玻璃拟态
    card: {
      background: colors.bgGlass,
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: 16,
      border: `1px solid ${colors.borderGlass}`,
      boxShadow: colors.shadowGlass,
      height: '100%',
      transition: 'all 0.3s ease',
      position: 'relative',
      zIndex: 1,
      overflow: 'hidden',
      '&:hover': {
        borderColor: colors.borderGlassHover,
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 32px rgba(14, 165, 233, 0.15)`,
      },
      // 顶部渐变装饰线
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: colors.gradientPrimary,
      },
    },
    cardHeader: {
      padding: theme.spacing(2, 2.5),
      borderBottom: `1px solid ${colors.borderGlass}`,
      '& .MuiCardHeader-title': {
        color: colors.textSecondary,
        fontSize: '0.7rem',
        fontWeight: 600,
        fontFamily: monoFont,
        letterSpacing: '2px',
        textTransform: 'uppercase',
      },
      '& .MuiCardHeader-action': {
        marginTop: 0,
        marginRight: 0,
      },
    },
    cardContent: {
      padding: theme.spacing(2.5),
    },
    
    // 概览卡片
    overviewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'repeat(2, 1fr)',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    overviewItem: {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(10px)',
      borderRadius: 12,
      padding: theme.spacing(2.5),
      textAlign: 'center',
      border: `1px solid rgba(14, 165, 233, 0.15)`,
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'rgba(14, 165, 233, 0.3)',
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 24px rgba(14, 165, 233, 0.15)',
      },
    },
    overviewLabel: {
      color: colors.textMuted,
      fontSize: '0.65rem',
      fontWeight: 600,
      fontFamily: monoFont,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      marginBottom: theme.spacing(1.5),
    },
    overviewValue: {
      fontSize: '2.5rem',
      fontWeight: 700,
      fontFamily: monoFont,
      lineHeight: 1,
      background: colors.gradientPrimary,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      textShadow: 'none',
    },
    overviewValueSuccess: {
      background: colors.gradientSuccess,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    overviewValueWarning: {
      background: colors.gradientWarning,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    overviewValueError: {
      background: colors.gradientError,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    overviewStatus: {
      fontSize: '0.7rem',
      fontFamily: monoFont,
      color: colors.textSecondary,
      marginTop: theme.spacing(1),
      letterSpacing: '0.5px',
    },
    
    // 指标进度条
    metricBar: {
      height: 6,
      borderRadius: 3,
      background: 'rgba(14, 165, 233, 0.1)',
      overflow: 'hidden',
      position: 'relative',
    },
    metricBarFill: {
      height: '100%',
      borderRadius: 3,
      transition: 'width 0.5s ease',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
        animation: '$shimmer 2s infinite',
      },
    },
    '@keyframes shimmer': {
      '0%': { transform: 'translateX(-100%)' },
      '100%': { transform: 'translateX(100%)' },
    },
    metricLabel: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(1),
    },
    metricName: {
      color: colors.textSecondary,
      fontSize: '0.7rem',
      fontFamily: monoFont,
      fontWeight: 500,
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    metricValue: {
      color: colors.textPrimary,
      fontSize: '0.85rem',
      fontFamily: monoFont,
      fontWeight: 600,
    },
    
    // 表格样式
    table: {
      '& .MuiTableCell-root': {
        borderBottom: `1px solid ${colors.borderGlass}`,
        color: colors.textPrimary,
        padding: theme.spacing(1.5, 2),
        fontFamily: monoFont,
        fontSize: '0.8rem',
      },
      '& .MuiTableHead-root .MuiTableCell-root': {
        background: 'rgba(255, 255, 255, 0.02)',
        color: colors.textMuted,
        fontSize: '0.65rem',
        fontWeight: 600,
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
      },
      '& .MuiTableBody-root .MuiTableRow-root': {
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: colors.bgGlassHover,
        },
      },
    },
    
    // 状态指示器 - 发光效果
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      display: 'inline-block',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: -2,
        left: -2,
        right: -2,
        bottom: -2,
        borderRadius: '50%',
        animation: '$pulse 2s ease-in-out infinite',
      },
    },
    '@keyframes pulse': {
      '0%, 100%': { opacity: 0.4, transform: 'scale(1)' },
      '50%': { opacity: 0.1, transform: 'scale(1.5)' },
    },
    statusRunning: {
      backgroundColor: colors.statusSuccess,
      boxShadow: `0 0 10px ${colors.statusSuccess}`,
      '&::after': {
        backgroundColor: colors.statusSuccess,
      },
    },
    statusPending: {
      backgroundColor: colors.statusWarning,
      boxShadow: `0 0 10px ${colors.statusWarning}`,
      '&::after': {
        backgroundColor: colors.statusWarning,
      },
    },
    statusFailed: {
      backgroundColor: colors.statusError,
      boxShadow: `0 0 10px ${colors.statusError}`,
      '&::after': {
        backgroundColor: colors.statusError,
      },
    },
    statusUnknown: {
      backgroundColor: colors.textMuted,
    },
    
    // 事件时间线
    timeline: {
      padding: 0,
      margin: 0,
      listStyle: 'none',
    },
    timelineItem: {
      display: 'flex',
      gap: theme.spacing(2),
      padding: theme.spacing(1.5, 0),
      borderBottom: `1px solid ${colors.borderGlass}`,
      transition: 'background-color 0.2s ease',
      '&:last-child': {
        borderBottom: 'none',
      },
      '&:hover': {
        backgroundColor: colors.bgGlassHover,
      },
    },
    timelineTime: {
      color: colors.textMuted,
      fontSize: '0.7rem',
      fontFamily: monoFont,
      minWidth: 60,
      letterSpacing: '0.5px',
    },
    timelineIcon: {
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.85rem',
    },
    timelineContent: {
      flex: 1,
    },
    timelineType: {
      fontSize: '0.65rem',
      fontWeight: 600,
      fontFamily: monoFont,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      marginBottom: 4,
    },
    timelineMessage: {
      color: colors.textSecondary,
      fontSize: '0.75rem',
      fontFamily: monoFont,
      lineHeight: 1.5,
    },
    
    // Deployment 卡片
    deploymentItem: {
      background: 'rgba(255, 255, 255, 0.5)',
      backdropFilter: 'blur(10px)',
      borderRadius: 12,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      border: `1px solid rgba(14, 165, 233, 0.12)`,
      transition: 'all 0.3s ease',
      '&:last-child': {
        marginBottom: 0,
      },
      '&:hover': {
        borderColor: 'rgba(14, 165, 233, 0.25)',
        background: 'rgba(255, 255, 255, 0.8)',
      },
    },
    deploymentHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
    },
    deploymentName: {
      color: colors.textPrimary,
      fontWeight: 600,
      fontSize: '0.9rem',
      fontFamily: monoFont,
      letterSpacing: '0.5px',
    },
    deploymentInfo: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: theme.spacing(1.5),
    },
    deploymentInfoItem: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.75rem',
      fontFamily: monoFont,
    },
    deploymentInfoLabel: {
      color: colors.textMuted,
      letterSpacing: '0.5px',
    },
    deploymentInfoValue: {
      color: colors.textSecondary,
    },
    
    // 刷新按钮
    refreshButton: {
      color: colors.textSecondary,
      background: 'rgba(255, 255, 255, 0.6)',
      border: `1px solid rgba(14, 165, 233, 0.2)`,
      transition: 'all 0.3s ease',
      '&:hover': {
        color: '#0ea5e9',
        background: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'rgba(14, 165, 233, 0.4)',
        boxShadow: '0 0 15px rgba(14, 165, 233, 0.2)',
      },
    },
    
    // 加载状态
    loading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(4),
      color: colors.textSecondary,
      fontFamily: monoFont,
      letterSpacing: '1px',
    },
    
    // 错误状态
    error: {
      background: 'rgba(239, 68, 68, 0.08)',
      border: `1px solid rgba(239, 68, 68, 0.25)`,
      borderRadius: 12,
      padding: theme.spacing(2),
      color: colors.statusError,
      fontFamily: monoFont,
    },
    
    // 空状态
    empty: {
      textAlign: 'center',
      padding: theme.spacing(4),
      color: colors.textMuted,
      fontFamily: monoFont,
      letterSpacing: '0.5px',
    },
    
    // 进度条容器
    progressBar: {
      height: 4,
      borderRadius: 2,
      background: 'rgba(14, 165, 233, 0.1)',
      overflow: 'hidden',
      marginBottom: theme.spacing(1.5),
    },
    progressBarFill: {
      height: '100%',
      borderRadius: 2,
      transition: 'width 0.5s ease',
    },
  })
);

/**
 * 获取状态颜色
 */
export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'running':
    case 'ready':
    case 'active':
    case 'succeeded':
      return colors.statusSuccess;
    case 'pending':
    case 'waiting':
    case 'progressing':
      return colors.statusWarning;
    case 'failed':
    case 'error':
    case 'crashloopbackoff':
      return colors.statusError;
    default:
      return colors.textMuted;
  }
}

/**
 * 获取状态渐变色
 */
export function getStatusGradient(status: string): string {
  switch (status.toLowerCase()) {
    case 'running':
    case 'ready':
    case 'active':
    case 'succeeded':
      return colors.gradientSuccess;
    case 'pending':
    case 'waiting':
    case 'progressing':
      return colors.gradientWarning;
    case 'failed':
    case 'error':
    case 'crashloopbackoff':
      return colors.gradientError;
    default:
      return colors.gradientPrimary;
  }
}

/**
 * 获取指标进度条渐变色
 */
export function getMetricGradient(percentage: number): string {
  if (percentage < 60) return colors.gradientSuccess;
  if (percentage < 80) return colors.gradientWarning;
  return colors.gradientError;
}
